import React, { useEffect, useState, useRef } from "react";
import { View } from "react-native";
import { useRouter } from "expo-router";
import { ClientType } from "@/types/db/clients";
import {
  EstimateCreateForm,
  EstimateCreateFormRef,
  EstimatePayload,
  ESTIMATE_STATUS_OPTIONS,
  getEstimateStatusColor,
} from "@/components/screens/estimate/NewEstimateForm";
import { EstimateStatusType } from "@/types/db/estimates";
import { useTheme } from "@/hooks/useTheme";
import { CreateHeader } from "@/components/screens/shared";
import useToast from "@/hooks/useToast";
import { estimateService } from "@/services/estimateService";
import useAuth from "@/hooks/useAuth";
import Head from "expo-router/head";
import { getSaveBehavior } from "@/lib/saveSettings";

export default function NewEstimatePage() {
  const toast = useToast();
  const router = useRouter();
  const { colors } = useTheme();
  const { tenantId } = useAuth();
  const [localDraft, setLocalDraft] = useState<EstimatePayload>();
  const [dirty, setDirty] = useState(false);
  const [sidebarClient, setSidebarClient] = useState<ClientType | null>(null);
  const [estimateName, setEstimateName] = useState("");
  const [status, setStatus] = useState<EstimateStatusType>("DRAFT");
  const formRef = useRef<EstimateCreateFormRef>(null);

  // Fetch next estimate number on mount
  useEffect(() => {
    const fetchNextNumber = async () => {
      try {
        const nextNumber = await estimateService.getNextEstimateNumber();
        setEstimateName(nextNumber);
        formRef.current?.updateEstimateName(nextNumber);
      } catch (error) {
        console.error("Failed to fetch next estimate number:", error);
      }
    };
    fetchNextNumber();
  }, []);

  const handleCreateEstimate = async (payload: any) => {
    if (!tenantId) return;
    try {
      const estimatePayload = payload.estimate;

      const estimateData = {
        tenant_id: tenantId,
        client_id: estimatePayload.client_id,
        project_id: estimatePayload.project_id,
        status: estimatePayload.status,
        meta_data: {
          estimate_number: estimatePayload.estimate_number,
          status: estimatePayload.status,
          line_items: [],
        },
      };

      // Create the estimate first
      const createdEstimate = await estimateService.addEstimate(estimateData);

      if (!createdEstimate?.id) {
        throw new Error("Failed to get estimate ID from created estimate");
      }

      // Upload pending files if any
      const pendingFiles = formRef.current?.getPendingFiles() || [];
      if (pendingFiles.length > 0) {
        toast.showToast({
          title: `Uploading ${pendingFiles.length} file(s)...`,
          toastType: "info",
        });

        let uploadedCount = 0;
        for (const attachmentFile of pendingFiles) {
          if (attachmentFile.file) {
            try {
              await estimateService.uploadFile(createdEstimate.id, attachmentFile.file, tenantId!);
              uploadedCount++;
            } catch (uploadErr) {
              console.error(`Failed to upload file ${attachmentFile.name}:`, uploadErr);
              // Continue with other files even if one fails
            }
          }
        }

        if (uploadedCount > 0) {
          toast.showToast({
            title: `Estimate created with ${uploadedCount} file(s) uploaded`,
            toastType: "success",
          });
        } else {
          toast.showToast({
            title: "Estimate created (file upload failed)",
            toastType: "warning",
          });
        }
      } else {
        toast.showToast({ title: "Estimate created successfully", toastType: "success" });
      }

      // Handle save behavior setting
      const behavior = await getSaveBehavior("estimate");
      switch (behavior) {
        case "view_created":
          router.setParams({ new: undefined, id: createdEstimate.id });
          break;
        case "create_another":
          handleReset();
          break;
        case "close":
        default:
          router.setParams({ new: undefined, panel: undefined });
          break;
      }
    } catch (err: any) {
      toast.showToast({
        title: "Failed to create estimate",
        message: err.message,
        toastType: "error",
      });
    }
  };

  // Sync estimateName and status with form state
  useEffect(() => {
    if (localDraft?.estimate?.estimate_number !== undefined && localDraft.estimate.estimate_number !== estimateName) {
      setEstimateName(localDraft.estimate.estimate_number);
    }
    if (localDraft?.estimate?.status !== undefined && localDraft.estimate.status !== status) {
      setStatus(localDraft.estimate.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localDraft?.estimate?.estimate_number, localDraft?.estimate?.status]);

  const handleReset = () => {
    setSidebarClient(null);
    setStatus("DRAFT");
    formRef.current?.handleReset();
    // Re-fetch estimate number after reset
    const fetchNextNumber = async () => {
      try {
        const nextNumber = await estimateService.getNextEstimateNumber();
        setEstimateName(nextNumber);
        formRef.current?.updateEstimateName(nextNumber);
      } catch (error) {
        console.error("Failed to fetch next estimate number:", error);
      }
    };
    fetchNextNumber();
  };

  return (
    <>
      <Head>
        <title>NEXUS New Estimate</title>
        <meta name="description" content="" />
      </Head>
      <View style={{ flex: 1 }}>
        <CreateHeader
          entityType="estimate"
          name={estimateName}
          nameEditable={false}
          namePlaceholder="Estimate #"
          subtitle={sidebarClient?.name}
          dirty={dirty}
          onSave={async () => {
            await formRef.current?.handleSave();
          }}
          onCancel={handleReset}
          status={status}
          onStatusChange={(value) => {
            setStatus(value as EstimateStatusType);
            formRef.current?.updateStatus(value);
          }}
          statusOptions={ESTIMATE_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getEstimateStatusColor(opt.text, colors),
          }))}
        />
        <View style={{ flex: 1, padding: 16 }}>
          <EstimateCreateForm
            ref={formRef}
            initialEstimate={{ estimate_number: estimateName }}
            onChange={(current) => {
              setLocalDraft(current);
            }}
            onSave={handleCreateEstimate}
            onDirtyChange={setDirty}
            onClientChange={setSidebarClient}
          />
        </View>
      </View>
    </>
  );
}
